import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  selectAddressBookError,
  selectInvalidAddresses,
  selectIsAddressBookLoading,
} from '../../../../store/selectors/addressBook';
import { IRow } from '../../../../types/addressBook';
import { NotificationListEnum } from '../../../../types/shell';
import { collectInvalidRows } from '../../../../utils/addressBook';
import { getCountLabel } from '../../../../utils/helpers';
import notification from '../../../../utils/notification';
import { ICsvUploadController } from '../csvUploadProfile';
import { useAddressUploadSuccess } from './useAddressUploadSuccess';
import { useTransformAddress } from './useTransformAddress';

export const useAddressBookCsvController = (): ICsvUploadController => {
  const { transformAddressesToRows, dispatchSubmit } = useTransformAddress();
  const { handleCloseWithSuccess, resetCount, addedAddressesLabel } = useAddressUploadSuccess();

  const isLoading = useSelector(selectIsAddressBookLoading);
  const invalidAddresses = useSelector(selectInvalidAddresses);
  const addressBookError = useSelector(selectAddressBookError);

  const importInvalidRows = useMemo<IRow[]>(
    () => (invalidAddresses && invalidAddresses.length > 0 ? transformAddressesToRows(invalidAddresses) : []),
    [invalidAddresses, transformAddressesToRows],
  );

  const hasImportError = Boolean(addressBookError);
  const isImportBlocked = importInvalidRows.length > 0 || isLoading;

  const onImportError = useCallback(() => {
    notification.error(NotificationListEnum.Error, {
      content: 'An error occurred while importing addresses. Please try again.',
    });
  }, []);

  const onImportInvalid = useCallback((count: number) => {
    notification.warning(NotificationListEnum.Warning, {
      content: `${getCountLabel(
        count,
        'address',
        'addresses',
      )} failed validation. Please fix the errors and try again.`,
    });
  }, []);

  return {
    validateRows: collectInvalidRows,
    submit: dispatchSubmit,
    isLoading,
    isImportBlocked,
    importInvalidRows,
    hasImportError,
    onImportError,
    onImportInvalid,
    onCloseWithSuccess: handleCloseWithSuccess,
    resetCount,
    successLabel: addedAddressesLabel,
  };
};
